import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { employeeListTourBridge } from '../services/employee-list-tour-bridge';

/**
 * Kontextuelle Hilfe-Tour fuer die GANZE Mitarbeiter-Seite (`/employee`,
 * `EmployeeListComponent` + `employee-advanced-table`). Wird ueber den
 * `<app-dialog-help-button>` neben dem Seitentitel gestartet (Pattern analog
 * `AVAILABILITY_CALENDAR_HELP_TOUR`).
 *
 * Erklaerend (nextCondition 'manual', Spotlight + Voice-Over). Gibt zuerst die
 * Grundfunktionen der Seite (Suche, Filter, Hinzufuegen, Werkzeuge, Tabelle)
 * und teasert am Ende JEDE Aktion des 3-Punkte-Menues an.
 *
 * Das 3-Punkte-Menue ist im echten DOM ein permissions-/rollen-konditionales
 * CDK-Overlay (z. B. "Berechtigungen verwalten" nur bei `Role === 3`). Fuer ein
 * verlaessliches Lehr-Highlighting von oben nach unten blenden wir stattdessen
 * ein tour-eigenes Lehr-Panel (Actions-Showcase) ein — ein normales in-page
 * Element, das IMMER alle Aktionen zeigt. Der `employeeListTourBridge` schaltet
 * es: `show` im row-menu-Step, `hide` davor (Reset / Back-Nav). Das End-Cleanup
 * (Tour-Abbruch/Abschluss) macht die Komponente via `activeTour$` — der
 * Coordinator ruft `onAfterHide` nicht auf.
 *
 * Cue-Daten (welches Element wann pulst) leben im Voice-Over-Manifest unter
 * `employee-list-help`; `hasCues` wird zur Laufzeit aus dem Audio-Manifest
 * gesetzt. Die einzelnen Filter-Chips, Werkzeug-Icons und Menue-Eintraege sind
 * Cue-Ziele (Anchors am DOM, kein eigener Step).
 *
 * Sieben Schritte:
 *  1. Ueberblick (ganze Seite)
 *  2. Suche
 *  3. Schnellfilter            (Cues: Manager, Teamleiter, Inaktiv)
 *  4. Mitarbeiter hinzufuegen
 *  5. Werkzeuge                (Cues: Spalten, Export, Import)
 *  6. Tabelle (Spalten/Klick)
 *  7. 3-Punkte-Menue           (Cues: 9 der 10 Aktionen von oben nach unten)
 *
 * Der Schaukasten zeigt seit der Personalakte ZEHN Aktionen, `row-menu.cues.json`
 * hat aber nur neun Cues: die Vertonung (row-menu.mp3) spricht „Personalakte"
 * nicht. Ein zehnter Cue mit erfundener Zeitmarke wuerde einen Eintrag pulsen,
 * waehrend von einem anderen die Rede ist — deshalb bleibt `employee-menu-documents`
 * bewusst ohne Cue, bis der Schritt neu vertont ist.
 */
export const EMPLOYEE_LIST_HELP_TOUR: TourConfig = {
  id: TourFeature.EmployeeListHelp,
  version: 1,
  title: 'EMPLOYEE_LIST_HELP_TOUR_TITLE',
  description: 'EMPLOYEE_LIST_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/employee',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 120,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'EMPLOYEE_LIST_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.EmployeeListView,
          spotlightAnchor: AnchorId.EmployeeListView,
          title: 'TUTORIAL.EMPLOYEE_LIST.WELCOME.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
          // Reset: falls das Lehr-Panel von einem vorherigen Lauf haengt.
          onBeforeShow: () => employeeListTourBridge.hideActionsShowcase(),
        },
        {
          id: 'search',
          anchor: AnchorId.EmployeeListSearch,
          spotlightAnchor: AnchorId.EmployeeListSearch,
          title: 'TUTORIAL.EMPLOYEE_LIST.SEARCH.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.SEARCH.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'filters',
          anchor: AnchorId.EmployeeListFilters,
          spotlightAnchor: AnchorId.EmployeeListFilters,
          title: 'TUTORIAL.EMPLOYEE_LIST.FILTERS.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.FILTERS.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'add',
          anchor: AnchorId.AddEmployeeButton,
          spotlightAnchor: AnchorId.AddEmployeeButton,
          title: 'TUTORIAL.EMPLOYEE_LIST.ADD.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.ADD.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="add-employee-button"]'),
        },
        {
          id: 'tools',
          anchor: AnchorId.EmployeeListToolbarTools,
          spotlightAnchor: AnchorId.EmployeeListToolbarTools,
          title: 'TUTORIAL.EMPLOYEE_LIST.TOOLS.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.TOOLS.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'table',
          anchor: AnchorId.EmployeeTable,
          spotlightAnchor: AnchorId.EmployeeTable,
          title: 'TUTORIAL.EMPLOYEE_LIST.TABLE.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.TABLE.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          // Back-Nav 7→6: Lehr-Panel wieder ausblenden, sonst bliebe es offen.
          onBeforeShow: () => employeeListTourBridge.hideActionsShowcase(),
        },
        {
          id: 'row-menu',
          // Spotlight auf das tour-eigene Lehr-Panel; zusaetzlich wird der echte
          // 3-Punkte-Button mitgerahmt, damit der User die Verbindung sieht.
          anchor: AnchorId.EmployeeActionsShowcase,
          spotlightAnchor: AnchorId.EmployeeActionsShowcase,
          additionalSpotlightAnchors: [AnchorId.EmployeeActionsMenu],
          title: 'TUTORIAL.EMPLOYEE_LIST.ROW_MENU.TITLE',
          content: 'TUTORIAL.EMPLOYEE_LIST.ROW_MENU.CONTENT',
          // Panel sitzt rechts → Popover links daneben, verdeckt es nicht.
          placement: 'left',
          nextCondition: 'manual',
          showNextButton: true,
          onBeforeShow: () => employeeListTourBridge.showActionsShowcase(),
        },
      ],
    },
  ],
};
